import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import BridgeClubLayout from '../../../components/BridgeClub/BridgeClubLayout';
import { bridgeClubAPI } from '../../../services/bridgeClubAPI';
import { useBridgeClubAuth } from '../../../contexts/BridgeClubAuthContext';
import EggLogModal from '../../../components/BridgeClub/FarmChallenge/EggLogModal';
import MilkLogModal from '../../../components/BridgeClub/FarmChallenge/MilkLogModal';
import WeightLogModal from '../../../components/BridgeClub/FarmChallenge/WeightLogModal';
import HarvestLogModal from '../../../components/BridgeClub/FarmChallenge/HarvestLogModal';
import './FarmChallengeHub.css';

interface ChallengeStats {
  current_balance: number;
  lifetime_earned: number;
  current_streak_weeks: number;
  longest_streak_weeks: number;
  current_multiplier: number;
  daily_remaining: number;
  week_points: number;
  recent_logs: ActivityLog[];
  opted_in: boolean;
  enrolled: boolean;
  last_activity_date?: string | null;
}

interface ActivityLog {
  id?: number;
  type: string;           // API returns 'type' not 'activity_type'
  date: string;           // API returns 'date' not 'activity_date'
  points: number;         // API returns 'points' not 'total_points'
  animal?: string | null; // API returns 'animal' not 'animal_name'
}

const FarmChallengeHub: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useBridgeClubAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<ChallengeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal states
  const [showEggModal, setShowEggModal] = useState(false);
  const [showMilkModal, setShowMilkModal] = useState(false);
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [showHarvestModal, setShowHarvestModal] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await bridgeClubAPI.getFarmChallengeStatus();
      setStats(data);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load farm challenge stats:', err);
      // Handle auth errors - redirect to login
      if (err.status === 401 || err.response?.status === 401) {
        navigate('/bridge-club/login', { replace: true });
        return;
      }
      // If 503 or feature disabled, show join page
      if (err.response?.status === 503) {
        setStats({ enrolled: false } as ChallengeStats);
      } else {
        setError('Failed to load challenge data');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    // Wait for auth check to complete before loading stats
    if (!authLoading && isAuthenticated) {
      loadStats();
    } else if (!authLoading && !isAuthenticated) {
      navigate('/bridge-club/login', { replace: true });
    }
  }, [authLoading, isAuthenticated, loadStats, navigate]);

  const handleLogComplete = () => {
    loadStats(); // Refresh stats after logging
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'egg_log': return '🥚';
      case 'milk_log': return '🥛';
      case 'weight_log': return '⚖️';
      case 'harvest_log': return '🔪';
      case 'purchase': return '🛒';
      case 'consistency_bonus': return '🔥';
      case 'streak_milestone': return '🏆';
      default: return '✨';
    }
  };

  const getActivityLabel = (type: string) => {
    switch (type) {
      case 'egg_log': return 'Egg Collection';
      case 'milk_log': return 'Milk Production';
      case 'weight_log': return 'Weight Log';
      case 'harvest_log': return 'Harvest';
      case 'purchase': return 'Purchase';
      case 'consistency_bonus': return 'Consistency Bonus';
      case 'streak_milestone': return 'Streak Milestone';
      default: return 'Activity';
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '';
    // Handle YYYY-MM-DD format by appending time to avoid timezone issues
    const date = new Date(dateStr + 'T12:00:00');
    if (isNaN(date.getTime())) return dateStr; // Fallback to raw string if parsing fails

    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (loading || authLoading) {
    return (
      <BridgeClubLayout>
        <div className="farm-challenge-loading">
          <div className="spinner"></div>
          <p>Loading Farm Challenge...</p>
        </div>
      </BridgeClubLayout>
    );
  }

  if (error) {
    return (
      <BridgeClubLayout>
        <div className="farm-challenge-error">
          <p>{error}</p>
          <button onClick={loadStats}>Try Again</button>
        </div>
      </BridgeClubLayout>
    );
  }

  // Not opted in - redirect to opt-in page
  if (stats && !stats.opted_in) {
    navigate('/bridge-club/farm-challenge/opt-in', { replace: true });
    return null;
  }

  // Opted in but not enrolled (edge case) - redirect to opt-in to complete enrollment
  if (stats && stats.opted_in && !stats.enrolled) {
    navigate('/bridge-club/farm-challenge/opt-in', { replace: true });
    return null;
  }

  const dollarValue = (stats?.current_balance || 0) / 100;

  return (
    <BridgeClubLayout>
      <div className="farm-challenge-hub">
        {/* Header */}
        <div className="hub-header">
          <span className="hub-icon">🌾</span>
          <h1>Farm Challenge</h1>
        </div>

        {/* Points Card */}
        <div className="points-card">
          <div className="points-main">
            <span className="points-balance">{stats?.current_balance?.toLocaleString() || 0}</span>
            <span className="points-label">points</span>
          </div>
          <div className="points-value-row">
            <span className="dollar-value">${dollarValue.toFixed(2)} value</span>
          </div>
          <div className="points-stats">
            {(stats?.current_streak_weeks || 0) > 0 && (
              <div className="stat-pill">
                <span>🔥</span>
                <span>{stats?.current_streak_weeks}-week streak</span>
              </div>
            )}
            <div className="stat-pill">
              <span>📈</span>
              <span>{stats?.current_multiplier || 1}x multiplier</span>
            </div>
          </div>
          {(stats?.current_balance || 0) >= 500 && (
            <Link to="/bridge-club/farm-challenge/redeem" className="redeem-btn">
              Redeem Points
            </Link>
          )}
        </div>

        {/* Quick Log Buttons */}
        <div className="quick-log-section">
          <h2>Quick Log</h2>
          <div className="quick-log-grid">
            <button className="log-btn" onClick={() => setShowEggModal(true)}>
              <span className="log-btn-icon">🥚</span>
              <span className="log-btn-label">Eggs</span>
            </button>
            <button className="log-btn" onClick={() => setShowMilkModal(true)}>
              <span className="log-btn-icon">🥛</span>
              <span className="log-btn-label">Milk</span>
            </button>
            <button className="log-btn" onClick={() => setShowWeightModal(true)}>
              <span className="log-btn-icon">⚖️</span>
              <span className="log-btn-label">Weight</span>
            </button>
            <button className="log-btn" onClick={() => setShowHarvestModal(true)}>
              <span className="log-btn-icon">🔪</span>
              <span className="log-btn-label">Harvest</span>
            </button>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="recent-activity-section">
          <div className="section-header">
            <h2>Recent Activity</h2>
            <Link to="/bridge-club/farm-challenge/history" className="see-all">
              See All
            </Link>
          </div>

          {stats?.recent_logs && stats.recent_logs.length > 0 ? (
            <div className="activity-list">
              {stats.recent_logs.slice(0, 5).map((log, index) => (
                <div key={log.id || index} className="activity-item">
                  <span className="activity-icon">{getActivityIcon(log.type)}</span>
                  <div className="activity-info">
                    <span className="activity-title">{getActivityLabel(log.type)}</span>
                    {log.animal && (
                      <span className="activity-animal">{log.animal}</span>
                    )}
                    <span className="activity-date">{formatDate(log.date)}</span>
                  </div>
                  <span className="activity-points">+{log.points}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="activity-empty">
              <p>No activity yet. Start logging to earn points!</p>
            </div>
          )}
        </div>

        {/* Quick Links */}
        <div className="quick-links">
          <Link to="/bridge-club/farm-challenge/history" className="quick-link">
            <span>📊</span>
            <span>Full History</span>
          </Link>
          <Link to="/bridge-club/farm-challenge/redeem" className="quick-link">
            <span>🎁</span>
            <span>Redeem</span>
          </Link>
        </div>
      </div>

      {/* Modals */}
      {showEggModal && (
        <EggLogModal
          onClose={() => setShowEggModal(false)}
          onSuccess={handleLogComplete}
        />
      )}
      {showMilkModal && (
        <MilkLogModal
          onClose={() => setShowMilkModal(false)}
          onSuccess={handleLogComplete}
        />
      )}
      {showWeightModal && (
        <WeightLogModal
          onClose={() => setShowWeightModal(false)}
          onSuccess={handleLogComplete}
        />
      )}
      {showHarvestModal && (
        <HarvestLogModal
          onClose={() => setShowHarvestModal(false)}
          onSuccess={handleLogComplete}
        />
      )}
    </BridgeClubLayout>
  );
};

export default FarmChallengeHub;
